import { useEditor, EditorContent } from "@tiptap/react";
import { deprecatedTones } from "@src/deprecatedDesignSystem/styles/deprecatedColors";
import BoldExtension from "@tiptap/extension-bold";
import BulletListExtension from "@tiptap/extension-bullet-list";
import DocumentExtension from "@tiptap/extension-document";
import { css, StyleSheet } from "aphrodite";
import HardBreakExtension from "@tiptap/extension-hard-break";
import HistoryExtension from "@tiptap/extension-history";
import ItalicExtension from "@tiptap/extension-italic";
import LinkExtension from "@tiptap/extension-link";
import ListItemExtension from "@tiptap/extension-list-item";
import OrderedListExtension from "@tiptap/extension-ordered-list";
import ParagraphExtension from "@tiptap/extension-paragraph";
import PlaceholderExtension from "@tiptap/extension-placeholder";
import HorizontalRuleExtension from "@tiptap/extension-horizontal-rule";
import Table from "@tiptap/extension-table";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TableRow from "@tiptap/extension-table-row";
import TextExtension from "@tiptap/extension-text";
import React, { FC } from "react";
import clsx from "clsx";
import { cn } from "@src/ui/lib/utils";
import { LIST_INDENTATION_TO_CLASS_NAME } from "../hooks/useRichTextEditorExtensions";

type Props = {
  value: string;
  className?: string;
  placeholder?: string;
  onChange?: (value: string) => void;
};

const SimpleRichTextEditor: FC<Props> = ({
  value,
  className = "",
  placeholder = "This module will teach you...",
  onChange,
}) => {
  const editor = useEditor({
    extensions: [
      TextExtension,
      ParagraphExtension,
      DocumentExtension,
      BoldExtension,
      ItalicExtension,
      HardBreakExtension,
      HorizontalRuleExtension,
      Table.configure({
        HTMLAttributes: {
          class: css(styles.richTextTable),
        },
      }),
      TableRow.configure(),
      TableHeader.configure(),
      TableCell.configure({
        HTMLAttributes: {
          class: css(styles.richTextTableInner),
        },
      }),
      OrderedListExtension.configure({
        HTMLAttributes: {
          class: clsx("list-decimal", LIST_INDENTATION_TO_CLASS_NAME[16]),
        },
      }),
      ListItemExtension.configure({
        HTMLAttributes: {
          class: LIST_INDENTATION_TO_CLASS_NAME[16],
        },
      }),
      BulletListExtension.configure({
        HTMLAttributes: {
          class: "list-disc",
        },
      }),
      HistoryExtension,
      LinkExtension.extend({ inclusive: true }).configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "rich-text-link",
        },
      }),
      PlaceholderExtension.configure({
        placeholder,
      }),
    ],
    content: value,
    editorProps: {
      attributes: {
        class: cn(
          "min-h-[100px] rounded-md border border-input bg-background px-3 py-2 text-[14px] tracking-[-0.006em]",
          "placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary",
          "disabled:cursor-not-allowed disabled:opacity-50",
          "max-h-[300px] overflow-y-auto", // Add this line for scrolling
          className,
        ),
      },
    },
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
  });
  return <EditorContent editor={editor} placeholder={placeholder} />;
};

const styles = StyleSheet.create({
  richTextTable: {
    border: `2px solid ${deprecatedTones.gray5Alpha}`,
    borderCollapse: "collapse",
    borderRadius: 12,
    width: "100%",
  },
  richTextTableInner: {
    border: `2px solid ${deprecatedTones.gray5Alpha}`,
    wordBreak: "break-word",
    minWidth: 60,
    minHeight: 60,
    padding: 4,
    fontSize: "14px",
    lineHeight: "18px",
  },
});

export default SimpleRichTextEditor;
